'use client';

import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { EASE_DEFAULT_MOTION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

/** Lué-style materialize: fade + de-blur (no vertical shift). */
const REVEAL_BLUR_PX = '4px';

const REVEAL_HIDDEN = {
  opacity: 0,
  filter: `blur(${REVEAL_BLUR_PX})`,
} as const;

const REVEAL_VISIBLE = {
  opacity: 1,
  filter: 'blur(0px)',
} as const;

const REVEAL_SPRING = {
  type: 'spring',
  stiffness: 60,
  damping: 10,
} as const;

const REVEAL_FILTER_TRANSITION = {
  duration: 0.4,
  ease: EASE_DEFAULT_MOTION,
} as const;

/**
 * Touch fallback — fade in WITHOUT the per-reveal de-blur animation (it's
 * GPU-heavy on weak mobile GPUs and fires during the scroll itself, MOR-707).
 *
 * `filter` is pinned to `blur(0px)` here rather than omitted: Motion leaves any
 * property that the `animate` target does NOT mention at its last value, and
 * because the pointer media-query is SSR-safe (false on first paint), the
 * element can mount with the desktop `initial` `blur(4px)` before resolving to
 * touch — omitting `filter` then left whole sections permanently blurred on
 * mobile. Pinning `blur(0px)` in both states clears that, and since reveal is
 * `blur(0px)→blur(0px)` it animates nothing (no per-reveal blur cost).
 */
const REVEAL_HIDDEN_OPACITY = { opacity: 0, filter: 'blur(0px)' } as const;
const REVEAL_VISIBLE_OPACITY = { opacity: 1, filter: 'blur(0px)' } as const;

/** Require more of the element in view before firing (avoids instant reveal on section edge). */
const VIEWPORT_REVEAL_THRESHOLD = 0.2;

/** Shrink the observed viewport from the bottom so content must scroll further in. */
const VIEWPORT_REVEAL_ROOT_MARGIN = '0px 0px -15% 0px';

/** Brief beat after intersection before the spring starts. */
const VIEWPORT_REVEAL_LEAD_IN_MS = 120;

/** Skip theme-alternate assets (e.g. `hidden dark:block`) — browsers do not load them. */
function isRenderableMediaElement(el: Element): boolean {
  if (!(el instanceof HTMLElement)) {
    return false;
  }
  const style = window.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

async function waitForImage(img: HTMLImageElement): Promise<void> {
  if (img.complete) {
    return;
  }
  await new Promise<void>((resolve) => {
    img.addEventListener('load', () => resolve(), { once: true });
    img.addEventListener('error', () => resolve(), { once: true });
  });
}

async function waitForVideo(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
    return;
  }
  await new Promise<void>((resolve) => {
    video.addEventListener('canplay', () => resolve(), { once: true });
    video.addEventListener('error', () => resolve(), { once: true });
  });
}

async function waitForDescendantMedia(node: HTMLElement): Promise<void> {
  const images = [...node.querySelectorAll('img')].filter(isRenderableMediaElement);
  const videos = [...node.querySelectorAll('video')].filter(isRenderableMediaElement);
  await Promise.all([...images.map(waitForImage), ...videos.map(waitForVideo)]);
}

export type LandingContentRevealProps = {
  children: ReactNode;
  revealed: boolean;
  /** Seconds before the spring starts. */
  delay?: number;
  className?: string;
  /** Hide from assistive tech while visually hidden (e.g. toggled header CTAs). */
  hideWhenHidden?: boolean;
};

/** Lué-style fade + de-blur driven by an external `revealed` flag. */
export function LandingContentReveal({
  children,
  revealed,
  delay = 0,
  className,
  hideWhenHidden = false,
}: LandingContentRevealProps): React.ReactNode {
  const reduceMotion = useReducedMotion();
  const coarsePointer = useMediaQuery('(pointer: coarse)');
  const hiddenState = coarsePointer ? REVEAL_HIDDEN_OPACITY : REVEAL_HIDDEN;
  const visibleState = coarsePointer ? REVEAL_VISIBLE_OPACITY : REVEAL_VISIBLE;

  return (
    <LazyMotion features={domAnimation} strict>
      <m.div
        className={cn(className)}
        initial={hiddenState}
        animate={revealed ? visibleState : hiddenState}
        transition={
          reduceMotion === true
            ? { duration: 0 }
            : {
                ...REVEAL_SPRING,
                delay,
                ...(coarsePointer ? {} : { filter: REVEAL_FILTER_TRANSITION }),
              }
        }
        aria-hidden={hideWhenHidden && !revealed ? true : undefined}
        inert={hideWhenHidden && !revealed ? true : undefined}
      >
        {children}
      </m.div>
    </LazyMotion>
  );
}

export type LandingSectionRevealProps = {
  children: ReactNode;
  /** Seconds before the spring starts (header stagger, section offset). */
  delay?: number;
  className?: string;
  /**
   * `viewport` — reveal once when scrolled into view (default).
   * `mount` — reveal after media preload without waiting for scroll (header).
   */
  trigger?: 'viewport' | 'mount';
};

function revealWhenReady(
  node: HTMLDivElement,
  trigger: NonNullable<LandingSectionRevealProps['trigger']>,
  reveal: () => void,
): () => void {
  let cancelled = false;
  let leadInTimer: ReturnType<typeof setTimeout> | null = null;
  const observer =
    trigger === 'viewport' && typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (entry?.isIntersecting !== true) return;
            observer?.disconnect();
            leadInTimer = setTimeout(() => {
              if (!cancelled) reveal();
            }, VIEWPORT_REVEAL_LEAD_IN_MS);
          },
          { threshold: VIEWPORT_REVEAL_THRESHOLD, rootMargin: VIEWPORT_REVEAL_ROOT_MARGIN },
        )
      : null;

  void waitForDescendantMedia(node).then(() => {
    if (cancelled) return;
    if (observer === null) {
      reveal();
      return;
    }
    observer.observe(node);
  });

  return (): void => {
    cancelled = true;
    if (leadInTimer !== null) clearTimeout(leadInTimer);
    observer?.disconnect();
  };
}

export function LandingSectionReveal({
  children,
  delay = 0,
  className,
  trigger = 'viewport',
}: LandingSectionRevealProps): React.ReactNode {
  const reduceMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(reduceMotion === true);

  useEffect(() => {
    if (reduceMotion === true) {
      return;
    }

    const node = ref.current;
    if (node === null) {
      return;
    }

    return revealWhenReady(node, trigger, () => setRevealed(true));
  }, [reduceMotion, trigger]);

  return (
    <div ref={ref} className={cn(className)}>
      <LandingContentReveal
        revealed={reduceMotion === true || revealed}
        delay={delay}
        {...(className !== undefined ? { className } : {})}
      >
        {children}
      </LandingContentReveal>
    </div>
  );
}
